/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import type { FC } from 'react';
import React, { useCallback, useContext, useEffect, useState, useMemo, useRef } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';
import { css } from '@compiled/react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, Stack, xcss, Text } from '@atlaskit/primitives';
import Heading from '@atlaskit/heading';
import CrossIcon from '@atlaskit/icon/core/cross';
import Image from '@atlaskit/image';
import { ModalTransition } from '@atlaskit/modal-dialog';
import Button, { IconButton } from '@atlaskit/button/new';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import VidPlayIcon from '@atlaskit/icon/core/video-play';
import AtlassianIntelligenceIcon from '@atlaskit/icon/core/atlassian-intelligence';
import AutomationIcon from '@atlaskit/icon/core/automation';
import CalendarIcon from '@atlaskit/icon/core/calendar';
import ChartTrendUpIcon from '@atlaskit/icon/core/chart-trend-up';
import OfficeBuildingIcon from '@atlaskit/icon/core/office-building';
import TableIcon from '@atlaskit/icon/core/table';
import TasksIcon from '@atlaskit/icon/core/tasks';
import WhiteboardIcon from '@atlaskit/icon/core/whiteboard';

import { usePopupDwellTime } from '@atlassian/popup-dwell-time';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { TransparentErrorBoundary } from '@confluence/error-boundary/entry-points/TransparentErrorBoundary.ts';
import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext.ts';
import { isErrorMarkedAsHandled } from '@confluence/graphql/entry-points/isErrorMarkedAsHandled.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { useIsCompanyHubEnabled } from '@confluence/company-hub-utils/entry-points/useIsCompanyHubEnabled.ts';
import { useSpaceKey } from '@confluence/space-utils/entry-points/useSpaceKey.ts';
import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { fg } from '@confluence/feature-gating';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';
import { mergeLiveDocI18n } from '@confluence/live-pages-utils/entry-points/mergeLiveDocI18n.ts';

import leftIllustration from '../images/Illustration.svg';

import type { OnboardingManualTrialModalQuery as OnboardingManualTrialModalQuery$data } from './__types__/OnboardingManualTrialModalQuery.ts';
import type { ListItemProps } from './ListItem.tsx';
import { ListItem } from './ListItem.tsx';
import { PremiumFeatureDeeplinks } from './PremiumFeatureDeeplinks.ts';
import { LearnMoreVideoModal } from './LearnMoreVideoModal.tsx';

const i18nBase = defineMessages({
	modalTitle: {
		id: 'premium-trial.onboarding.mt.modal.title',
		description:
			'The text is shown as the main heading at the top of the Premium trial onboarding manual trial modal when it first opens. It welcomes the user into their Premium trial and sets the context for exploring key features. There are no placeholders in this message. Explains the purpose of the onboarding experience.',
		defaultMessage: 'Welcome to your Premium trial',
	},
	modalDescription: {
		id: 'premium-trial.onboarding.mt.modal.description',
		description:
			'The text is shown as the subtitle body copy under the main heading in the Premium trial onboarding manual trial modal when it first opens. It encourages the user to get started by exploring the five most popular Premium features. There are no placeholders in this message. Explains how to begin the guided feature exploration.',
		defaultMessage: 'Get started by exploring our 5 most popular features.',
	},
	videoButtonText: {
		id: 'premium-trial.onboarding.mt.video-button.text',
		description:
			'The text is shown on the secondary call-to-action button at the bottom of the Premium trial onboarding manual trial modal when it is displayed. It opens a separate video modal that summarizes Premium. There are no placeholders in this message. Explains the action to watch an overview video.',
		defaultMessage: 'Watch a summary of Premium',
	},
	/* features */
	aiTitle: {
		id: 'premium-trial.onboarding.mt.ai.title',
		description:
			'The text is shown as the heading for the AI feature card in the Premium trial onboarding modal. It labels the Atlassian Intelligence feature that is highlighted among the five most popular Premium features.',
		defaultMessage: 'Atlassian Intelligence (AI)',
	},
	aiDesciption: {
		id: 'premium-trial.onboarding.mt.ai.description',
		description:
			'The text is shown as the body description for the AI feature card in the Premium trial onboarding modal. It explains the value of Atlassian Intelligence by highlighting AI-powered editing and content summarization capabilities.',
		defaultMessage:
			'Work smarter by leveraging AI-powered editing experiences to create and summarize content.',
	},
	aiAction: {
		id: 'premium-trial.onboarding.mt.ai.action',
		description:
			'The text is shown on a link/button in the AI feature card in the Premium trial onboarding modal. Clicking this link navigates the user to the AI feature to explore and start using Atlassian Intelligence.',
		defaultMessage: 'Discover the power of AI',
	},
	companyHubTitle: {
		id: 'premium-trial.onboarding.mt.company-hub.title',
		description:
			'The text is shown as the heading for the Company Hub feature card in the Premium trial onboarding modal. It identifies the Company Hub feature as one of the five most popular Premium features.',
		defaultMessage: 'Company Hub',
	},
	companyHubDescription: {
		id: 'premium-trial.onboarding.mt.company-hub.description',
		description: 'Description for company hub feature',
		defaultMessage:
			'Help your teams stay connected with a centralized intranet for key announcements and resources.',
	},
	companyHubAction: {
		id: 'premium-trial.onboarding.mt.company-hub.action',
		description: 'Action for company hub feature',
		defaultMessage: 'Customize my company hub',
	},
	automationTitle: {
		id: 'premium-trial.onboarding.mt.automation.title',
		description:
			'The text is shown as the heading for the Automation feature card in the Premium trial onboarding modal. It identifies the Automation feature as one of the five most popular Premium features.',
		defaultMessage: 'Automation',
	},
	automationDescription: {
		id: 'premium-trial.onboarding.mt.automation.description',
		description: 'Description for automation feature',
		defaultMessage:
			'Save time by creating rules to change content status, create a Jira task, and more.',
	},
	automationAction: {
		id: 'premium-trial.onboarding.mt.automation.action',
		description:
			'The text is shown on a link/button in the Automation feature card in the Premium trial onboarding modal. Clicking this link navigates the user to create and build an automation rule for Confluence content.',
		defaultMessage: 'Build an automation rule',
	},
	missionControlTitle: {
		id: 'premium-trial.onboarding.mt.mission-control.title',
		description: 'Title for mission control feature',
		defaultMessage: 'Mission control',
	},
	missionControlDescription: {
		id: 'premium-trial.onboarding.mt.mission-control.description',
		description: 'Description for mission control feature',
		defaultMessage:
			'Boost your team’s productivity with insights and controls for data-driven improvements.',
	},
	missionControlAction: {
		id: 'premium-trial.onboarding.mt.mission-control.action',
		description: 'Action for mission control feature',
		defaultMessage: 'Go to mission control',
	},
	contentManagerTitle: {
		id: 'premium-trial.onboarding.mt.content-manager.title',
		description: 'Title for content manager feature',
		defaultMessage: 'Content manager',
	},
	contentManagerDescription: {
		id: 'premium-trial.onboarding.mt.content-manager.description',
		description: 'Description for content manager feature',
		defaultMessage:
			'Keep content organized and make bulk changes across hundreds of pages simultaneously.',
	},
	contentManagerAction: {
		id: 'premium-trial.onboarding.mt.content-manager.action',
		description: 'Action for content manager feature',
		defaultMessage: 'View my content manager',
	},
	teamCalendarsTitle: {
		id: 'premium-trial.onboarding.mt.team-calendars.title',
		description: 'Title for team calendars feature',
		defaultMessage: 'Team calendars',
	},
	teamCalendarsDescription: {
		id: 'premium-trial.onboarding.mt.team-calendars.description',
		description: 'Description for team calendars feature',
		defaultMessage: 'Keep one single source of truth for your team’s schedules and due dates.',
	},
	teamCalendarsAction: {
		id: 'premium-trial.onboarding.mt.team-calendars.action',
		description: 'Action for team calendars feature',
		defaultMessage: 'Go to team calendars',
	},
	unlimitedWhiteBoardsTitle: {
		id: 'premium-trial.onboarding.mt.unlimited-whiteboards.title',
		description: 'Title for unlimited whiteboards feature',
		defaultMessage: 'Whiteboards and Jira without limits',
	},
	unlimitedWhiteBoardsDescription: {
		id: 'premium-trial.onboarding.mt.unlimited-whiteboards.description',
		description: 'Description for unlimited whiteboards feature',
		defaultMessage:
			'Brainstorm without boundaries and turn ideas into Jira tasks from one single location.',
	},
	unlimitedWhiteBoardsAction: {
		id: 'premium-trial.onboarding.mt.unlimited-whiteboards.action',
		description: 'Action for unlimited whiteboards feature',
		defaultMessage: 'Create a whiteboard',
	},
	analyticsTitle: {
		id: 'premium-trial.onboarding.mt.analytics.title',
		description:
			'The text is shown as the heading for the Analytics feature card in the Premium trial onboarding modal. It identifies the Analytics feature as one of the five most popular Premium features.',
		defaultMessage: 'Analytics',
	},
	analyticsDescription: {
		id: 'premium-trial.onboarding.mt.analytics.description',
		description: 'Description for analytics feature',
		defaultMessage: 'See user engagement and views at the site, space, and content level.',
	},
	analyticsAction: {
		id: 'premium-trial.onboarding.mt.analytics.action',
		description:
			'The text is shown on a link/button in the Analytics feature card in the Premium trial onboarding modal. Clicking this link navigates the user to view analytics and engagement metrics for their Confluence site and content.',
		defaultMessage: 'View analytics',
	},
});

const i18nLiveDocs = defineMessages({
	contentManagerDescription: {
		id: 'premium-trial.onboarding.mt.content-manager.description.livedocs',
		defaultMessage:
			'Keep content organized and make bulk changes across hundreds of pages and live docs simultaneously.',
		description: 'Updated version of premium-trial.onboarding.mt.content-manager.description',
	},
});

const i18n = mergeLiveDocI18n(i18nBase, i18nLiveDocs);

const imageContainerStyles = xcss({
	width: '145',
	minInlineSize: 'max-content',
});

const imageStyles = css({
	objectFit: 'cover',
	minHeight: '100%',
	width: '168px',
	height: '400px',
});

export enum MODAL_TRIGGER {
	ONBOARDING = 'onboarding',
}

type ModalProps = {
	onClose?: () => void;
	stop?: () => void;
	onCloseComplete?: () => void;
	modalTrigger?: MODAL_TRIGGER;
};

enum Features {
	AI = 'atlassianIntelligence',
	TEAM_CALENDARS = 'teamCalendars',
	COMPANY_HUB = 'companyHub',
	UNLIMITED_WHITEBOARDS = 'unlimitedWhiteboards',
	AUTOMATION = 'automation',
	MISSION_CONTROL = 'missionControl',
	CONTENT_MANAGER = 'contentManager',
	ANALYTICS = 'analytics',
}

const featuresMapper: Array<
	Omit<ListItemProps, 'link' | 'key'> & {
		key: Features;
		showToAdmin?: boolean;
		showToEndUser?: boolean;
	}
> = [
	{
		key: Features.AI,
		title: i18n.aiTitle,
		bodyText: i18n.aiDesciption,
		linkText: i18n.aiAction,
		icon: AtlassianIntelligenceIcon,
		showToAdmin: true,
		showToEndUser: true,
	},
	{
		key: Features.TEAM_CALENDARS,
		title: i18n.teamCalendarsTitle,
		bodyText: i18n.teamCalendarsDescription,
		linkText: i18n.teamCalendarsAction,
		icon: CalendarIcon,
		showToAdmin: false,
		showToEndUser: true,
	},
	{
		key: Features.COMPANY_HUB,
		title: i18n.companyHubTitle,
		bodyText: i18n.companyHubDescription,
		linkText: i18n.companyHubAction,
		icon: OfficeBuildingIcon,
		showToAdmin: true,
		showToEndUser: false,
	},
	{
		key: Features.UNLIMITED_WHITEBOARDS,
		title: i18n.unlimitedWhiteBoardsTitle,
		bodyText: i18n.unlimitedWhiteBoardsDescription,
		linkText: i18n.unlimitedWhiteBoardsAction,
		icon: WhiteboardIcon,
		showToAdmin: false,
		showToEndUser: true,
	},
	{
		key: Features.AUTOMATION,
		title: i18n.automationTitle,
		bodyText: i18n.automationDescription,
		linkText: i18n.automationAction,
		icon: AutomationIcon,
		showToAdmin: true,
		showToEndUser: true,
	},
	{
		key: Features.MISSION_CONTROL,
		title: i18n.missionControlTitle,
		bodyText: i18n.missionControlDescription,
		linkText: i18n.missionControlAction,
		icon: TableIcon,
		showToAdmin: true,
		showToEndUser: false,
	},
	{
		key: Features.CONTENT_MANAGER,
		title: i18n.contentManagerTitle,
		bodyText: i18n.contentManagerDescription,
		linkText: i18n.contentManagerAction,
		icon: TasksIcon,
		showToAdmin: true,
		showToEndUser: false,
	},
	{
		key: Features.ANALYTICS,
		title: i18n.analyticsTitle,
		bodyText: i18n.analyticsDescription,
		linkText: i18n.analyticsAction,
		icon: ChartTrendUpIcon,
		showToAdmin: false,
		showToEndUser: true,
	},
];

export const MODAL_NAME = 'premiumOnboardingManualTrialModal';

const modalClosedLocalStorageKey = 'premium.trial.onboarding.mt.modal.closed';

export const OnboardingManualTrialModal: FC<ModalProps> = ({
	onClose,
	stop,
	onCloseComplete,
	modalTrigger = MODAL_TRIGGER.ONBOARDING,
}) => {
	const [isOpen, setIsOpen] = useState<boolean>(true);
	const [isLearnMoreVideoOpen, setIsLearnMoreVideoIsOpen] = useState(false);
	const { Modal } = useChoreographerAssets();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { isSiteAdmin } = useContext(SPAViewContext);
	const { isCompanyHubEntryPointEnabled } = useIsCompanyHubEnabled();
	const currentSpaceKey = useSpaceKey();

	const { data: { personalSpace, popularSpaces, starredSpace, recentSpaces } = {}, error } =
		useQuery<OnboardingManualTrialModalQuery$data>(gql`
			query OnboardingManualTrialModalQuery {
				personalSpace: confluence_atlassianUser(current: true) {
					id
					confluence {
						hasPersonalSpace
						space {
							id
							alias
						}
					}
				}
				popularSpaces: popularFeed(first: 25) {
					nodes {
						content {
							id
							space {
								id
								alias
								type
							}
						}
					}
				}
				starredSpace: spaces(favourite: true, first: 1, status: "current") {
					nodes {
						id
						alias
					}
				}
				recentSpaces: recentlyViewedSpaces {
					id
					alias
				}
			}
		`);
	const personalSpaceKey =
		(personalSpace?.confluence?.hasPersonalSpace && personalSpace?.confluence?.space?.alias) ||
		undefined;
	const popularSpace = popularSpaces?.nodes.find((node) => node.content?.space?.type !== 'system');
	const popularSpaceKey = popularSpace?.content?.space?.alias || undefined;
	const starredSpaceKey = starredSpace?.nodes?.[0]?.alias || undefined;
	const recentSpaceKey = recentSpaces?.[0]?.alias || undefined;

	if (error && !isErrorMarkedAsHandled(error)) {
		markErrorAsHandled(error);
	}

	const premiumFeatureDeeplinks = useMemo(() => {
		return new PremiumFeatureDeeplinks({
			personalSpaceKey,
			popularSpaceKey,
			starredSpaceKey,
			recentSpaceKey,
			currentSpaceKey: currentSpaceKey || undefined,
		});
	}, [personalSpaceKey, popularSpaceKey, starredSpaceKey, recentSpaceKey, currentSpaceKey]);

	const featureLinksMapper: Record<Features, string> = useMemo(() => {
		return {
			[Features.AI]: premiumFeatureDeeplinks.getAi(),
			[Features.TEAM_CALENDARS]: premiumFeatureDeeplinks.getTeamCalendars(),
			[Features.COMPANY_HUB]: premiumFeatureDeeplinks.getCompanyHub({
				isCompanyHubEnabled: isCompanyHubEntryPointEnabled,
			}),
			[Features.UNLIMITED_WHITEBOARDS]: premiumFeatureDeeplinks.getWhiteboard({
				source: MODAL_NAME,
			}),
			[Features.AUTOMATION]: isSiteAdmin
				? premiumFeatureDeeplinks.getAutomationAdmin()
				: premiumFeatureDeeplinks.getAutomationEndUser(),
			[Features.MISSION_CONTROL]: premiumFeatureDeeplinks.getMissionControl(),
			[Features.CONTENT_MANAGER]: premiumFeatureDeeplinks.getContentManager(),
			[Features.ANALYTICS]: premiumFeatureDeeplinks.getAnalytics(),
		};
	}, [isCompanyHubEntryPointEnabled, isSiteAdmin, premiumFeatureDeeplinks]);

	const featuresToShow = featuresMapper.filter(
		(feature) => (isSiteAdmin && feature.showToAdmin) || (!isSiteAdmin && feature.showToEndUser),
	);

	if (fg('a4c_free_and_standard_editions')) {
		const automationIdx = featuresToShow.findIndex(
			(feature) => feature.key === Features.AUTOMATION,
		);
		featuresToShow.splice(automationIdx, 1);
	}

	const modalRef = useRef<HTMLDivElement | null>(null);
	const { getDwellTime } = usePopupDwellTime(modalRef);

	const hasLinkEventFiredWithDwellTime = useRef<boolean>(false);

	const closeModal = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'ptoManualTrialModalCloseButton',
				source: MODAL_NAME,
				attributes: {
					dwellTime: getDwellTime(),
					modalTrigger,
				},
			},
		}).fire();
		setIsOpen(false);
		onClose?.();
	}, [createAnalyticsEvent, getDwellTime, onClose, modalTrigger]);

	const openLearnMoreVideo = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'premiumVideoModalButton',
				source: MODAL_NAME,
				attributes: {
					dwellTime: getDwellTime(),
					modalTrigger,
				},
			},
		}).fire();

		setIsLearnMoreVideoIsOpen(true);
	}, [createAnalyticsEvent, getDwellTime, modalTrigger]);

	const closeLearnMoreVideo = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'premiumVideoModalCloseButton',
				source: MODAL_NAME,
				attributes: {
					modalTrigger,
				},
			},
		}).fire();

		setIsLearnMoreVideoIsOpen(false);
	}, [createAnalyticsEvent, modalTrigger]);

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: MODAL_NAME,
				attributes: {
					isSiteAdmin,
					modalTrigger,
				},
			},
		}).fire();
	}, [createAnalyticsEvent, isSiteAdmin, modalTrigger]);

	const onLinkClick = useCallback(
		(feature: Features) => {
			let dwellTime = {};
			if (!hasLinkEventFiredWithDwellTime.current) {
				dwellTime = { dwellTime: getDwellTime() };
				hasLinkEventFiredWithDwellTime.current = true;
			}

			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'link',
					actionSubjectId: 'premiumFeatureDeeplink',
					source: MODAL_NAME,
					attributes: {
						feature,
						isSiteAdmin,
						...dwellTime,
						modalTrigger,
					},
				},
			}).fire();
			stop?.();
			modalTrigger === MODAL_TRIGGER.ONBOARDING &&
				localStorage.setItem(modalClosedLocalStorageKey, 'true');
		},
		[createAnalyticsEvent, isSiteAdmin, stop, getDwellTime, modalTrigger],
	);

	if (
		modalTrigger === MODAL_TRIGGER.ONBOARDING &&
		localStorage.getItem(modalClosedLocalStorageKey)
	) {
		return null;
	}

	return (
		<TransparentErrorBoundary attribution={Attribution.FLYWHEEL_ENGAGEMENT}>
			<ModalTransition>
				{isLearnMoreVideoOpen && <LearnMoreVideoModal onClose={closeLearnMoreVideo} />}
			</ModalTransition>
			<ModalTransition>
				{isOpen && (
					<Modal
						autoFocus={false}
						width={752}
						shouldScrollInViewport
						onCloseComplete={onCloseComplete}
					>
						<Inline ref={modalRef} alignBlock="stretch">
							<Box xcss={imageContainerStyles}>
								<Image
									src={leftIllustration}
									srcDark={leftIllustration}
									css={imageStyles}
									// TODO: Update the attribute below so it is translated.
									// eslint-disable-next-line no-restricted-syntax
									alt="Premium trial onboarding modal"
								/>
							</Box>
							<Box padding="space.300">
								<Inline alignInline="end">
									<IconButton
										appearance="subtle"
										icon={CrossIcon}
										onClick={closeModal}
										label="Close Modal"
									/>
								</Inline>
								<Box paddingInline="space.200">
									<Stack space="space.300">
										<Stack space="space.100">
											<Heading size="medium" as="h1">
												<FormattedMessage {...i18n.modalTitle} />
											</Heading>
											<Text as="p">
												<FormattedMessage {...i18n.modalDescription} />
											</Text>
										</Stack>
										<Stack space="space.300">
											{featuresToShow.map((feature) => (
												<ListItem
													label={feature.key}
													bodyText={feature.bodyText}
													linkText={feature.linkText}
													link={featureLinksMapper[feature.key]}
													icon={feature.icon}
													title={feature.title}
													onLinkClick={() => onLinkClick(feature.key)}
													key={feature.key}
												/>
											))}
										</Stack>
										<Inline alignInline="end">
											<Button iconBefore={VidPlayIcon} onClick={openLearnMoreVideo}>
												<FormattedMessage {...i18n.videoButtonText} />
											</Button>
										</Inline>
									</Stack>
								</Box>
							</Box>
						</Inline>
					</Modal>
				)}
			</ModalTransition>
		</TransparentErrorBoundary>
	);
};
